import React from "react";
import { Alert, Box, Container, LinearProgress, Stack, Tooltip, IconButton } from "@mui/material";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

import PushOptInCard from "./PushOptInCard";
import ProfileAddressesCard from "./profile/ProfileAddressesCard";
import ProfileBanksCard from "./profile/ProfileBanksCard";
import ProfileCompanyCard from "./profile/ProfileCompanyCard";
import ProfileHeader from "./profile/ProfileHeader";
import ProfileSaveBar from "./profile/ProfileSaveBar";
import ProfileSnackbar from "./profile/ProfileSnackbar";
import ProfileTaxCard from "./profile/ProfileTaxCard";
import useProfileData from "./profile/useProfileData";

export default function Profile() {
  const {
    profileData,
    logoUploading,
    logoPreviews,
    logoLoading,
    uploadAsPrimary,
    displayedLogos,
    loading,
    loadError,
    fieldErrors,
    addressErrors,
    bankErrors,
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    sectionsOpen,
    emailError,
    phoneError,
    economicIdError,
    taxIdError,
    vatError,
    setUploadAsPrimary,
    setSnackbarOpen,
    handleInputChange,
    handleAddressChange,
    handleBankChange,
    handleSubmit,
    handleLogoUpload,
    handleSetPrimaryLogo,
    handleDeleteLogo,
    toggleSection,
    reload,
  } = useProfileData();

  const renderCollapseAction = (key, extraContent = null) => (
    <Stack direction="row" spacing={1} alignItems="center">
      {extraContent}
      <Tooltip title={sectionsOpen[key] ? "Bereich einklappen" : "Bereich ausklappen"}>
        <IconButton
          onClick={() => toggleSection(key)}
          aria-expanded={sectionsOpen[key]}
          aria-label={sectionsOpen[key] ? "Bereich einklappen" : "Bereich ausklappen"}
          size="small"
        >
          {sectionsOpen[key] ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <>
      <ProfileHeader onReload={reload} />

      <Container maxWidth="lg" sx={{ mt: 4, pb: 6 }}>
        {loading && (
          <Box sx={{ my: 3 }}>
            <LinearProgress />
          </Box>
        )}
        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {!loading && !loadError && (
          <Box component="form" onSubmit={handleSubmit}>
            <ProfileCompanyCard
              open={sectionsOpen.company}
              onToggle={(extra) => renderCollapseAction("company", extra)}
              profileData={profileData}
              displayedLogos={displayedLogos}
              logoPreviews={logoPreviews}
              logoLoading={logoLoading}
              logoUploading={logoUploading}
              uploadAsPrimary={uploadAsPrimary}
              onUploadAsPrimaryChange={setUploadAsPrimary}
              onLogoUpload={handleLogoUpload}
              onSetPrimaryLogo={handleSetPrimaryLogo}
              onDeleteLogo={handleDeleteLogo}
              fieldErrors={fieldErrors}
              emailError={emailError}
              phoneError={phoneError}
              onInputChange={handleInputChange}
            />

            <ProfileTaxCard
              open={sectionsOpen.tax}
              onToggle={() => renderCollapseAction("tax")}
              profileData={profileData}
              fieldErrors={fieldErrors}
              taxIdError={taxIdError}
              economicIdError={economicIdError}
              vatError={vatError}
              onInputChange={handleInputChange}
            />

            <ProfileAddressesCard
              open={sectionsOpen.addresses}
              onToggle={() => renderCollapseAction("addresses")}
              profileData={profileData}
              addressErrors={addressErrors}
              onAddressChange={handleAddressChange}
            />

            <ProfileBanksCard
              open={sectionsOpen.banks}
              onToggle={() => renderCollapseAction("banks")}
              profileData={profileData}
              bankErrors={bankErrors}
              onBankChange={handleBankChange}
            />

            <Box sx={{ mb: 3 }}>
              <PushOptInCard />
            </Box>

            <ProfileSaveBar onReload={reload} />
          </Box>
        )}

        <ProfileSnackbar
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
      </Container>
    </>
  );
}
